import { Modal, Form, Button, InputGroup, Spinner, Image, Badge } from 'react-bootstrap';
import { useEffect, useState, useRef } from 'react';
import { apiUrl } from '../../../config/config';
import Swal from "sweetalert2";
import moment from 'moment';
import { Card } from "../../../components/card/card.jsx";

const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});

const USER_STATUS = { INACTIVE: 0, ACTIVE: 1 };

const initialFormState = {
    email: '',
    password: '',
    completeName: '',
    phone: '',
    active: USER_STATUS.ACTIVE,
    role: { idRole: '', codRole: '', nameRole: '', active: 1 },
    profileImageUrl: null,
    identificationNumber: ''
};

const initialSanctionState = {
    reason: '',
    startDate: '',
    endDate: ''
};

const initialLicenseFormState = {
    licenseType: 'ANNUAL',
    modality: '',
    eventId: '',
    eventDateId: ''
};

function UserDetailModal({ show, onClose, userSelected, fectchData }) {
    const [user, setUser] = useState(initialFormState);
    const [roles, setRoles] = useState([]);
    const [showPassword, setShowPassword] = useState(false);

    // Estados para licencias
    const [licenses, setLicenses] = useState([]);
    const [loadingLicenses, setLoadingLicenses] = useState(false);
    const [editingLicense, setEditingLicense] = useState(null);
    const [savingLicense, setSavingLicense] = useState(false);
    const [showLicenseForm, setShowLicenseForm] = useState(false);
    const [newLicense, setNewLicense] = useState(initialLicenseFormState);

    // Estados para modal de detalle de licencia
    const [showLicenseDetail, setShowLicenseDetail] = useState(false);
    const [selectedLicenseDetail, setSelectedLicenseDetail] = useState(null);

    // Estados para eventos (para licencias tipo EVENT)
    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [selectedEventDates, setSelectedEventDates] = useState([]);

    const [savingUser, setSavingUser] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // Estados para sanciones
    const [sanctions, setSanctions] = useState([]);
    const [loadingSanctions, setLoadingSanctions] = useState(false);
    const [newSanction, setNewSanction] = useState(initialSanctionState);
    const [editingSanction, setEditingSanction] = useState(null);
    const [savingSanction, setSavingSanction] = useState(false);
    const [showSanctionForm, setShowSanctionForm] = useState(false);

    const userEmail = JSON.parse(window.localStorage.getItem("userProfile")).email;

    // Cargar roles activos
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const res = await fetch(`${apiUrl}/roles?status=1`);
                const data = await res.json();
                if (data.ok) setRoles(data.data || []);
            } catch (err) {
                console.error("Error cargando roles", err);
            }
        };
        fetchRoles();
    }, []);

    // Cargar eventos activos del año actual
    useEffect(() => {
        const fetchEvents = async () => {
            setLoadingEvents(true);
            try {
                const res = await fetch(`${apiUrl}/events`);
                const data = await res.json();
                if (data.ok) {
                    const currentYear = new Date().getFullYear();
                    const currentYearEvents = data.events.filter(e => e.yearEvent === currentYear);
                    setEvents(currentYearEvents || []);
                }
            } catch (err) {
                console.error("Error cargando eventos", err);
            } finally {
                setLoadingEvents(false);
            }
        };
        if (show) {
            fetchEvents();
        }
    }, [show]);

    // Cargar datos del usuario, licencias y sanciones
    useEffect(() => {
        if (userSelected) {
            const { id, email, completeName, phone, active, createdAt, createdBy, role, password, profileImageUrl, identificationNumber } = userSelected;
            setUser({
                id,
                email,
                completeName,
                phone,
                active,
                createdAt,
                createdBy,
                role,
                password,
                profileImageUrl,
                identificationNumber
            });
            setImagePreview(profileImageUrl);
            setSelectedImage(null);

            // Cargar licencias
            const fetchLicenses = async () => {
                setLoadingLicenses(true);
                try {
                    const res = await fetch(`${apiUrl}/licenses/user/${id}`);
                    const data = await res.json();
                    if (data.ok) setLicenses(data.data || []);
                    else setLicenses([]);
                } catch (error) {
                    console.error("Error cargando licencias:", error);
                    setLicenses([]);
                } finally {
                    setLoadingLicenses(false);
                }
            };

            // Cargar sanciones
            const fetchSanctions = async () => {
                setLoadingSanctions(true);
                try {
                    const res = await fetch(`${apiUrl}/sanctions/user/${id}`);
                    const data = await res.json();
                    if (data.ok) setSanctions(data.data || []);
                    else setSanctions([]);
                } catch (error) {
                    console.error("Error cargando sanciones:", error);
                    setSanctions([]);
                } finally {
                    setLoadingSanctions(false);
                }
            };

            fetchLicenses();
            fetchSanctions();
        } else {
            setUser(initialFormState);
            setLicenses([]);
            setSanctions([]);
            setImagePreview(null);
            setSelectedImage(null);
            setNewSanction(initialSanctionState);
            setNewLicense(initialLicenseFormState);
            setEditingSanction(null);
            setEditingLicense(null);
            setShowSanctionForm(false);
            setShowLicenseForm(false);
        }
    }, [userSelected]);

    // Cuando cambia el tipo de licencia en el formulario
    useEffect(() => {
        if (newLicense.licenseType === 'ANNUAL') {
            setNewLicense(prev => ({
                ...prev,
                eventId: '',
                eventDateId: ''
            }));
            setSelectedEventDates([]);
        }
    }, [newLicense.licenseType]);

    // Cuando cambia el evento seleccionado
    useEffect(() => {
        if (newLicense.eventId) {
            const selectedEvent = events.find(e => e.idEvent === parseInt(newLicense.eventId));
            setSelectedEventDates(selectedEvent?.dates || []);
            setNewLicense(prev => ({ ...prev, eventDateId: '' }));
        } else {
            setSelectedEventDates([]);
        }
    }, [newLicense.eventId, events]);

    // Manejar selección de imagen
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                Toast.fire({ icon: "error", title: "Por favor selecciona un archivo de imagen válido" });
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                Toast.fire({ icon: "error", title: "La imagen no debe superar los 5MB" });
                return;
            }
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Eliminar imagen
    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(user.id ? user.profileImageUrl : null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Guardar cambios en usuario
    const handleSaveUser = async () => {
        setSavingUser(true);
        try {
            const method = user.id ? "PUT" : "POST";
            const endpoint = user.id ? `${apiUrl}/update-user/${user.id}` : `${apiUrl}/register`;

            const formData = new FormData();
            formData.append('email', user.email);
            formData.append('identificationNumber', user.identificationNumber);
            formData.append('password', user.password);
            formData.append('completeName', user.completeName);
            formData.append('phone', user.phone || '');
            formData.append('active', user.active);
            formData.append('createdBy', user.id ? user.createdBy : userEmail);
            formData.append('role', JSON.stringify(user.role));

            if (selectedImage) {
                formData.append('profileImage', selectedImage);
            }

            const response = await fetch(endpoint, {
                method,
                body: formData
            });

            const data = await response.json();

            if (data.ok) {
                await fectchData();
                Toast.fire({
                    icon: "success",
                    title: `Usuario ${user.id ? "actualizado" : "creado"} con éxito`,
                });
                onClose();
            } else {
                const { message } = data;
                Toast.fire({
                    icon: "error",
                    title: `Error al ${user.id ? "actualizar" : "crear"} el usuario`,
                    text: Array.isArray(message) ? message.join("\n") : message,
                });
            }

        } catch (error) {
            console.error(`Error al ${user.id ? "actualizar" : "crear"} el usuario:`, error);
            Toast.fire({
                icon: "error",
                title: `Error al ${user.id ? "actualizar" : "crear"} el usuario`,
            });
        } finally {
            setSavingUser(false);
        }
    };

    // =============== FUNCIONES DE LICENCIAS ===============

    // Ver detalle de licencia
    const handleViewLicenseDetail = (license) => {
        setSelectedLicenseDetail(license);
        setShowLicenseDetail(true);
    };

    // Crear nueva licencia
    const handleCreateLicense = async () => {
        if (!user.id) return;

        // Validaciones
        if (!newLicense.licenseType) {
            Toast.fire({ icon: "warning", title: "Selecciona el tipo de licencia" });
            return;
        }

        if (!newLicense.modality) {
            Toast.fire({ icon: "warning", title: "La modalidad es obligatoria" });
            return;
        }

        if (newLicense.licenseType === 'EVENT') {
            if (!newLicense.eventId) {
                Toast.fire({ icon: "warning", title: "Selecciona un evento" });
                return;
            }
            if (!newLicense.eventDateId) {
                Toast.fire({ icon: "warning", title: "Selecciona una fecha del evento" });
                return;
            }
        }

        setSavingLicense(true);
        try {
            const res = await fetch(`${apiUrl}/licenses/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.id,
                    createdBy: userEmail,
                    licenseType: newLicense.licenseType,
                    modality: newLicense.modality,
                    eventId: newLicense.licenseType === 'EVENT' ? parseInt(newLicense.eventId) : null,
                    eventDateId: newLicense.licenseType === 'EVENT' ? parseInt(newLicense.eventDateId) : null
                })
            });

            const data = await res.json();

            if (data.ok) {
                Toast.fire({ icon: "success", title: "Licencia creada correctamente" });
                // Recargar licencias
                const licensesRes = await fetch(`${apiUrl}/licenses/user/${user.id}`);
                const licensesData = await licensesRes.json();
                if (licensesData.ok) {
                    setLicenses(licensesData.data && licensesData.data.length > 0 ? licensesData.data : []);
                } else {
                    setLicenses([]);
                }

                // Limpiar formulario
                setNewLicense(initialLicenseFormState);
                setShowLicenseForm(false);
                await fectchData();
            } else {
                const { message } = data;
                Toast.fire({
                    icon: "error",
                    title: "Error al crear la licencia",
                    text: Array.isArray(message) ? message.join("\n") : message
                });
            }
        } catch (error) {
            console.error("Error creando licencia:", error);
            Toast.fire({ icon: "error", title: "Error al crear la licencia" });
        } finally {
            setSavingLicense(false);
        }
    };

    // Auto-calcular fecha de expiración al cambiar issueDate
    const handleIssueDateChange = (dateStr, license) => {
        const issue = moment(dateStr);
        const expiration = issue.add(1, "year").format("YYYY-MM-DD");
        setEditingLicense({
            ...license,
            issueDate: dateStr,
            expirationDate: expiration
        });
    };

    // Actualizar licencia
    const handleUpdateLicense = async () => {
        if (!editingLicense) return;

        if (!editingLicense.modality) {
            Toast.fire({ icon: "warning", title: "La modalidad es obligatoria" });
            return;
        }

        setSavingLicense(true);
        try {
            const response = await fetch(`${apiUrl}/licenses/${editingLicense.id}/user/${user.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    issueDate: editingLicense.issueDate ? moment(editingLicense.issueDate).format('YYYY-MM-DD') : null,
                    expirationDate: editingLicense.expirationDate ? moment(editingLicense.expirationDate).format('YYYY-MM-DD') : null,
                    status: editingLicense.status,
                    modality: editingLicense.modality,
                    updatedBy: userEmail
                })
            });
            const data = await response.json();

            if (data.ok) {
                Toast.fire({ icon: "success", title: "Licencia actualizada correctamente" });
                // Recargar licencias
                const licensesRes = await fetch(`${apiUrl}/licenses/user/${user.id}`);
                const licensesData = await licensesRes.json();
                if (licensesData.ok) {
                    setLicenses(licensesData.data && licensesData.data.length > 0 ? licensesData.data : []);
                } else {
                    setLicenses([]);
                }

                setEditingLicense(null);
                await fectchData();
            } else {
                Toast.fire({ icon: "error", title: "Error al actualizar la licencia" });
            }
        } catch (err) {
            console.error("Error actualizando licencia:", err);
            Toast.fire({ icon: "error", title: "Error al actualizar la licencia" });
        } finally {
            setSavingLicense(false);
        }
    };

    // Eliminar licencia
    const handleDeleteLicense = async (licenseId) => {
        const confirm = await Swal.fire({
            title: "¿Eliminar licencia?",
            text: "Esta acción no se puede deshacer.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar"
        });

        if (confirm.isConfirmed) {
            setSavingLicense(true);
            try {
                const response = await fetch(`${apiUrl}/licenses/${licenseId}`, {
                    method: "DELETE",
                });
                const data = await response.json();

                if (data.ok) {
                    Toast.fire({ icon: "success", title: "Licencia eliminada correctamente" });
                    // Recargar licencias
                    const licensesRes = await fetch(`${apiUrl}/licenses/user/${user.id}`);
                    const licensesData = await licensesRes.json();
                    if (licensesData.ok) {
                        setLicenses(licensesData.data && licensesData.data.length > 0 ? licensesData.data : []);
                    } else {
                        setLicenses([]);
                    }
                    await fectchData();
                } else {
                    Toast.fire({ icon: "error", title: "Error al eliminar la licencia" });
                }
            } catch (error) {
                console.error("Error eliminando licencia:", error);
                Toast.fire({ icon: "error", title: "Error al eliminar la licencia" });
            } finally {
                setSavingLicense(false);
            }
        }
    };

    // Iniciar edición de licencia
    const handleStartEditLicense = (license) => {
        setEditingLicense({
            id: license.id,
            licenseCode: license.licenseCode,
            licenseType: license.licenseType,
            modality: license.modality,
            validationToken: license.validationToken,
            status: license.status,
            issueDate: license.issueDate ? moment(license.issueDate).format('YYYY-MM-DD') : '',
            expirationDate: license.expirationDate ? moment(license.expirationDate).format('YYYY-MM-DD') : '',
            isEventLicense: license.licenseType === 'EVENT'
        });
    };

    // Cancelar edición de licencia
    const handleCancelEditLicense = () => {
        setEditingLicense(null);
    };

    // Determinar estado de licencia
    const getLicenseStatusBadge = (license) => {
        const status = license.status;
        const statusMap = {
            'ACTIVE': { bg: 'success', text: 'Activa' },
            'PENDING_PAYMENT': { bg: 'warning', text: 'Pendiente de pago' },
            'EXPIRED': { bg: 'danger', text: 'Expirada' },
            'SUSPENDED': { bg: 'secondary', text: 'Suspendida' },
            'CANCELLED': { bg: 'dark', text: 'Cancelada' }
        };
        const statusInfo = statusMap[status] || { bg: 'secondary', text: status };
        return <Badge bg={statusInfo.bg}>{statusInfo.text}</Badge>;
    };

    // Obtener badge de tipo de licencia
    const getLicenseTypeBadge = (licenseType) => {
        const typeMap = {
            'ANNUAL': { bg: 'primary', text: 'Anual' },
            'EVENT': { bg: 'info', text: 'Por Evento' }
        };
        const typeInfo = typeMap[licenseType] || { bg: 'secondary', text: licenseType };
        return <Badge bg={typeInfo.bg}>{typeInfo.text}</Badge>;
    };

    // =============== FUNCIONES DE SANCIONES ===============

    // Crear nueva sanción
    const handleCreateSanction = async () => {
        if (!user.id) return;

        if (!newSanction.reason || !newSanction.startDate || !newSanction.endDate) {
            Toast.fire({ icon: "warning", title: "Por favor completa todos los campos de la sanción" });
            return;
        }

        if (new Date(newSanction.startDate) > new Date(newSanction.endDate)) {
            Toast.fire({ icon: "error", title: "La fecha de inicio no puede ser mayor que la fecha de fin" });
            return;
        }

        setSavingSanction(true);
        try {
            const response = await fetch(`${apiUrl}/sanctions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userSanction: user.id,
                    reason: newSanction.reason,
                    startDate: newSanction.startDate,
                    endDate: newSanction.endDate,
                    createdBy: userEmail
                })
            });

            const data = await response.json();

            if (data.ok) {
                Toast.fire({ icon: "success", title: "Sanción creada correctamente" });
                const res = await fetch(`${apiUrl}/sanctions/user/${user.id}`);
                const sanctionsData = await res.json();
                if (sanctionsData.ok) setSanctions(sanctionsData.data || []);

                setNewSanction(initialSanctionState);
                setShowSanctionForm(false);
                await fectchData();
            } else {
                const { message } = data;
                Toast.fire({
                    icon: "error",
                    title: "Error al crear la sanción",
                    text: Array.isArray(message) ? message.join("\n") : message,
                });
            }
        } catch (error) {
            console.error("Error creando sanción:", error);
            Toast.fire({ icon: "error", title: "Error al crear la sanción" });
        } finally {
            setSavingSanction(false);
        }
    };

    // Actualizar sanción
    const handleUpdateSanction = async () => {
        if (!editingSanction) return;

        if (!editingSanction.reason || !editingSanction.startDate || !editingSanction.endDate) {
            Toast.fire({ icon: "warning", title: "Por favor completa todos los campos de la sanción" });
            return;
        }

        if (new Date(editingSanction.startDate) > new Date(editingSanction.endDate)) {
            Toast.fire({ icon: "error", title: "La fecha de inicio no puede ser mayor que la fecha de fin" });
            return;
        }

        setSavingSanction(true);
        try {
            const response = await fetch(`${apiUrl}/sanctions/${editingSanction.idSanction}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    reason: editingSanction.reason,
                    startDate: editingSanction.startDate,
                    endDate: editingSanction.endDate,
                    updatedBy: userEmail
                })
            });

            const data = await response.json();

            if (data.ok) {
                Toast.fire({ icon: "success", title: "Sanción actualizada correctamente" });
                const res = await fetch(`${apiUrl}/sanctions/user/${user.id}`);
                const sanctionsData = await res.json();
                if (sanctionsData.ok) setSanctions(sanctionsData.data || []);

                setEditingSanction(null);
                await fectchData();
            } else {
                const { message } = data;
                Toast.fire({
                    icon: "error",
                    title: "Error al actualizar la sanción",
                    text: Array.isArray(message) ? message.join("\n") : message,
                });
            }
        } catch (error) {
            console.error("Error actualizando sanción:", error);
            Toast.fire({ icon: "error", title: "Error al actualizar la sanción" });
        } finally {
            setSavingSanction(false);
        }
    };

    // Eliminar sanción
    const handleDeleteSanction = async (sanctionId) => {
        const confirm = await Swal.fire({
            title: "¿Eliminar sanción?",
            text: "Esta acción no se puede deshacer.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar"
        });

        if (confirm.isConfirmed) {
            setSavingSanction(true);
            try {
                const response = await fetch(`${apiUrl}/sanctions/${sanctionId}`, {
                    method: "DELETE",
                });
                const data = await response.json();

                if (data.ok) {
                    Toast.fire({ icon: "success", title: "Sanción eliminada correctamente" });
                    const res = await fetch(`${apiUrl}/sanctions/user/${user.id}`);
                    const sanctionsData = await res.json();
                    if (sanctionsData.ok) setSanctions(sanctionsData.data || []);
                    await fectchData();
                } else {
                    Toast.fire({ icon: "error", title: "Error al eliminar la sanción" });
                }
            } catch (error) {
                console.error("Error eliminando sanción:", error);
                Toast.fire({ icon: "error", title: "Error al eliminar la sanción" });
            } finally {
                setSavingSanction(false);
            }
        }
    };

    // Iniciar edición de sanción
    const handleStartEditSanction = (sanction) => {
        setEditingSanction({
            idSanction: sanction.idSanction,
            reason: sanction.reason,
            startDate: moment(sanction.startDate).format('YYYY-MM-DD'),
            endDate: moment(sanction.endDate).format('YYYY-MM-DD')
        });
    };

    // Cancelar edición
    const handleCancelEdit = () => {
        setEditingSanction(null);
    };

    // Determinar si una sanción está activa
    const isSanctionActive = (sanction) => {
        const now = new Date();
        const start = new Date(sanction.startDate);
        const end = new Date(sanction.endDate);
        return start <= now && end >= now;
    };

    return (
        <>
            <Modal show={show} onHide={onClose} size="lg" backdropClassName='custom-backdrop'>
                <div className='modal-content card shadow-lg'>
                    <Modal.Header closeButton>
                        <Modal.Title>{user.id ? "Detalles del usuario" : "Nuevo usuario"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                        {/* DATOS DEL USUARIO */}
                        <h6 className="fw-bold mb-3">Datos del usuario</h6>
                        <Form>
                            {/* Imagen de perfil */}
                            <Form.Group className="mb-4">
                                <Form.Label>Imagen de perfil</Form.Label>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="position-relative">
                                        {imagePreview ? (
                                            <Image
                                                src={imagePreview}
                                                roundedCircle
                                                style={{
                                                    width: '120px',
                                                    height: '120px',
                                                    objectFit: 'cover',
                                                    border: '3px solid #dee2e6'
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center bg-light rounded-circle"
                                                style={{
                                                    width: '120px',
                                                    height: '120px',
                                                    border: '3px solid #dee2e6'
                                                }}
                                            >
                                                <i className="bi bi-person-circle" style={{ fontSize: '4rem', color: '#adb5bd' }}></i>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-grow-1">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleImageChange}
                                            accept="image/*"
                                            className="d-none"
                                            id="profileImageInput"
                                        />
                                        <label htmlFor="profileImageInput" className="btn btn-outline-primary btn-sm me-2">
                                            <i className="bi bi-upload me-2"></i>
                                            Seleccionar imagen
                                        </label>
                                        {(imagePreview || selectedImage) && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={handleRemoveImage}
                                            >
                                                <i className="bi bi-trash me-2"></i>
                                                Eliminar
                                            </Button>
                                        )}
                                        <small className="d-block text-muted mt-2">
                                            Formatos permitidos: JPG, PNG, GIF. Tamaño máximo: 5MB
                                        </small>
                                    </div>
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Correo electrónico</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={user.email || ""}
                                    onChange={(e) => setUser({ ...user, email: e.target.value })}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Número de identificación</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={user.identificationNumber || ""}
                                    onChange={(e) => setUser({ ...user, identificationNumber: e.target.value })}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Contraseña</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? "text" : "password"}
                                        value={user.password || ""}
                                        onChange={(e) => setUser({ ...user, password: e.target.value })}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
                                    </Button>
                                </InputGroup>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Nombre completo</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={user.completeName || ""}
                                    onChange={(e) => setUser({ ...user, completeName: e.target.value })}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Teléfono</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={user.phone || ""}
                                    onChange={(e) => setUser({ ...user, phone: e.target.value })}
                                />
                            </Form.Group>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label>Rol</Form.Label>
                                        <Form.Select
                                            value={user.role?.idRole || ""}
                                            onChange={(e) => {
                                                const selectedRole = roles.find(r => r.idRole === Number(e.target.value));
                                                setUser({ ...user, role: selectedRole || null });
                                            }}
                                        >
                                            <option value="">Seleccione un rol...</option>
                                            {roles.map(role => (
                                                <option key={role.idRole} value={role.idRole}>
                                                    {role.nameRole}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label>Estado</Form.Label>
                                        <Form.Select
                                            value={user.active}
                                            onChange={(e) => setUser({ ...user, active: parseInt(e.target.value) })}
                                        >
                                            <option value={1}>Activo</option>
                                            <option value={0}>Inactivo</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </div>

                            <div className="text-end mt-4">
                                <Button variant="primary" onClick={handleSaveUser} disabled={savingUser}>
                                    {savingUser ? <Spinner animation="border" size="sm" /> : "Guardar usuario"}
                                </Button>
                            </div>
                        </Form>

                        {/* LICENCIAS */}
                        {user.id && (
                            <>
                                <hr className="my-4" />
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0">Licencias</h6>
                                    {!editingLicense && !showLicenseForm && (
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => setShowLicenseForm(true)}
                                            disabled={savingLicense}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Nueva licencia
                                        </Button>
                                    )}
                                </div>

                                {loadingLicenses ? (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" /> Cargando licencias...
                                    </div>
                                ) : (
                                    <>
                                        {/* Formulario de nueva licencia */}
                                        {showLicenseForm && (
                                            <Card >
                                                <div className="card-body">
                                                    <h6 className="card-title mb-3">Nueva Licencia</h6>
                                                    <Form>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Tipo de Licencia</Form.Label>
                                                            <Form.Select
                                                                value={newLicense.licenseType}
                                                                onChange={(e) => setNewLicense({ ...newLicense, licenseType: e.target.value })}
                                                            >
                                                                <option value="ANNUAL">Anual</option>
                                                                <option value="EVENT">Por Evento</option>
                                                            </Form.Select>
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Modalidad</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                value={newLicense.modality}
                                                                onChange={(e) => setNewLicense({ ...newLicense, modality: e.target.value })}
                                                                placeholder="Ej: Experto, Principiante, Modalidad 1..."
                                                            />
                                                            <Form.Text className="text-muted">
                                                                Especifica la categoría o modalidad de la licencia
                                                            </Form.Text>
                                                        </Form.Group>

                                                        {newLicense.licenseType === 'EVENT' && (
                                                            <>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label>Evento</Form.Label>
                                                                    <Form.Select
                                                                        value={newLicense.eventId}
                                                                        onChange={(e) => setNewLicense({ ...newLicense, eventId: e.target.value })}
                                                                        disabled={loadingEvents}
                                                                    >
                                                                        <option value="">Seleccione un evento...</option>
                                                                        {events.map(event => (
                                                                            <option key={event.idEvent} value={event.idEvent}>
                                                                                {event.titleEvent} ({event.yearEvent})
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>
                                                                    {events.length === 0 && !loadingEvents && (
                                                                        <Form.Text className="text-danger">
                                                                            No hay eventos activos para el año actual
                                                                        </Form.Text>
                                                                    )}
                                                                </Form.Group>

                                                                {newLicense.eventId && selectedEventDates.length > 0 && (
                                                                    <Form.Group className="mb-3">
                                                                        <Form.Label>Fecha del Evento</Form.Label>
                                                                        <Form.Select
                                                                            value={newLicense.eventDateId}
                                                                            onChange={(e) => setNewLicense({ ...newLicense, eventDateId: e.target.value })}
                                                                        >
                                                                            <option value="">Seleccione una fecha...</option>
                                                                            {selectedEventDates
                                                                                .sort((a, b) => new Date(a.dateEvent) - new Date(b.dateEvent))
                                                                                .map(date => (
                                                                                    <option key={date.idDateEvent} value={date.idDateEvent}>
                                                                                        {moment(date.dateEvent).format('DD/MM/YYYY')}
                                                                                    </option>
                                                                                ))}
                                                                        </Form.Select>
                                                                    </Form.Group>
                                                                )}
                                                            </>
                                                        )}

                                                        <div className="d-flex justify-content-end gap-2 mt-3">
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setShowLicenseForm(false);
                                                                    setNewLicense(initialLicenseFormState);
                                                                }}
                                                                disabled={savingLicense}
                                                            >
                                                                Cancelar
                                                            </Button>
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={handleCreateLicense}
                                                                disabled={savingLicense}
                                                            >
                                                                {savingLicense ? <Spinner animation="border" size="sm" /> : "Crear licencia"}
                                                            </Button>
                                                        </div>
                                                    </Form>
                                                </div>
                                            </Card>
                                        )}

                                        {/* Lista de licencias */}
                                        {licenses.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover text-nowrap">
                                                    <thead>
                                                        <tr>
                                                            <th>Código</th>
                                                            <th>Tipo</th>
                                                            <th>Modalidad</th>
                                                            <th>Estado</th>
                                                            <th>Emisión</th>
                                                            <th>Expiración</th>
                                                            <th style={{ width: '150px' }}>Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {licenses.map((license) => (
                                                            editingLicense && editingLicense.id === license.id ? (
                                                                // Modo edición
                                                                <tr key={license.id}>
                                                                    <td className="align-middle">
                                                                        <small className="text-muted">{license.licenseCode}</small>
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {getLicenseTypeBadge(license.licenseType)}
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="text"
                                                                            size="sm"
                                                                            value={editingLicense.modality}
                                                                            onChange={(e) => setEditingLicense({ ...editingLicense, modality: e.target.value })}
                                                                            placeholder="Modalidad"
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Select
                                                                            size="sm"
                                                                            value={editingLicense.status}
                                                                            onChange={(e) => setEditingLicense({ ...editingLicense, status: e.target.value })}
                                                                        >
                                                                            <option value="PENDING_PAYMENT">Pendiente de pago</option>
                                                                            <option value="ACTIVE">Activa</option>
                                                                            <option value="EXPIRED">Expirada</option>
                                                                            <option value="SUSPENDED">Suspendida</option>
                                                                            <option value="CANCELLED">Cancelada</option>
                                                                        </Form.Select>
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="date"
                                                                            size="sm"
                                                                            value={editingLicense.issueDate}
                                                                            onChange={(e) => handleIssueDateChange(e.target.value, editingLicense)}
                                                                            disabled={editingLicense.isEventLicense}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="date"
                                                                            size="sm"
                                                                            value={editingLicense.expirationDate}
                                                                            readOnly
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-1">
                                                                            <button
                                                                                className="btn btn-sm btn-outline-success"
                                                                                onClick={handleUpdateLicense}
                                                                                disabled={savingLicense}
                                                                            >
                                                                                <i className="fa fa-check"></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-outline-secondary"
                                                                                onClick={handleCancelEditLicense}
                                                                                disabled={savingLicense}
                                                                            >
                                                                                <i className="fa fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                // Modo vista
                                                                <tr key={license.id}>
                                                                    <td className="align-middle">
                                                                        <small className="text-muted">{license.licenseCode}</small>
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {getLicenseTypeBadge(license.licenseType)}
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        <div style={{
                                                                            maxWidth: '120px',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}>
                                                                            {license.modality || <small className="text-muted">N/A</small>}
                                                                        </div>
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {getLicenseStatusBadge(license)}
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {license.issueDate ? moment(license.issueDate).format('DD/MM/YYYY') : 'N/A'}
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {license.expirationDate ? moment(license.expirationDate).format('DD/MM/YYYY') : 'N/A'}
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-1">
                                                                            <button
                                                                                className="btn btn-sm btn-outline-info"
                                                                                onClick={() => handleViewLicenseDetail(license)}
                                                                                title="Ver detalle"
                                                                            >
                                                                                <i className="fa fa-eye"></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                onClick={() => handleStartEditLicense(license)}
                                                                                disabled={savingLicense || showLicenseForm}
                                                                                title="Editar"
                                                                            >
                                                                                <i className="fa fa-edit"></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                onClick={() => handleDeleteLicense(license.id)}
                                                                                disabled={savingLicense}
                                                                                title="Eliminar"
                                                                            >
                                                                                <i className="fa fa-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            !showLicenseForm && (
                                                <div className="text-center py-4 text-muted">
                                                    <i className="fa fa-id-card" style={{ fontSize: '2rem' }}></i>
                                                    <p className="mb-0 mt-2">No hay licencias registradas para este usuario</p>
                                                </div>
                                            )
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {/* SANCIONES */}
                        {user.id && (
                            <>
                                <hr className="my-4" />
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0">Sanciones</h6>
                                    {!showSanctionForm && !editingSanction && (
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => setShowSanctionForm(true)}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Nueva sanción
                                        </Button>
                                    )}
                                </div>

                                {loadingSanctions ? (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" /> Cargando sanciones...
                                    </div>
                                ) : (
                                    <>
                                        {/* Formulario de nueva sanción */}
                                        {showSanctionForm && (
                                            <Card>
                                                <div className="card-body">
                                                    <h6 className="card-title mb-3">Nueva sanción</h6>
                                                    <Form>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Razón de la sanción</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                value={newSanction.reason}
                                                                onChange={(e) => setNewSanction({ ...newSanction, reason: e.target.value })}
                                                                placeholder="Describe la razón de la sanción..."
                                                            />
                                                        </Form.Group>
                                                        <div className="row g-3">
                                                            <div className="col-md-6">
                                                                <Form.Group>
                                                                    <Form.Label>Fecha de inicio</Form.Label>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={newSanction.startDate}
                                                                        onChange={(e) => setNewSanction({ ...newSanction, startDate: e.target.value })}
                                                                    />
                                                                </Form.Group>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <Form.Group>
                                                                    <Form.Label>Fecha de fin</Form.Label>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={newSanction.endDate}
                                                                        onChange={(e) => setNewSanction({ ...newSanction, endDate: e.target.value })}
                                                                    />
                                                                </Form.Group>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex justify-content-end gap-2 mt-3">
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setShowSanctionForm(false);
                                                                    setNewSanction(initialSanctionState);
                                                                }}
                                                                disabled={savingSanction}
                                                            >
                                                                Cancelar
                                                            </Button>
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={handleCreateSanction}
                                                                disabled={savingSanction}
                                                            >
                                                                {savingSanction ? <Spinner animation="border" size="sm" /> : "Guardar sanción"}
                                                            </Button>
                                                        </div>
                                                    </Form>
                                                </div>
                                            </Card>
                                        )}

                                        {/* Lista de sanciones */}
                                        {sanctions.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover text-nowrap">
                                                    <thead>
                                                        <tr>
                                                            <th>Código</th>
                                                            <th>Razón</th>
                                                            <th>Fecha inicio</th>
                                                            <th>Fecha fin</th>
                                                            <th>Estado</th>
                                                            <th style={{ width: '120px' }}>Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sanctions.map((sanction) => (
                                                            editingSanction && editingSanction.idSanction === sanction.idSanction ? (
                                                                // Modo edición
                                                                <tr key={sanction.idSanction}>
                                                                    <td className="align-middle">
                                                                        <small className="text-muted">{sanction.code}</small>
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            as="textarea"
                                                                            rows={1}
                                                                            size="sm"
                                                                            value={editingSanction.reason}
                                                                            onChange={(e) => setEditingSanction({ ...editingSanction, reason: e.target.value })}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="date"
                                                                            size="sm"
                                                                            value={editingSanction.startDate}
                                                                            onChange={(e) => setEditingSanction({ ...editingSanction, startDate: e.target.value })}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="date"
                                                                            size="sm"
                                                                            value={editingSanction.endDate}
                                                                            onChange={(e) => setEditingSanction({ ...editingSanction, endDate: e.target.value })}
                                                                        />
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {isSanctionActive(sanction) ? (
                                                                            <Badge bg="danger">Activa</Badge>
                                                                        ) : (
                                                                            <Badge bg="secondary">Inactiva</Badge>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-1">
                                                                            <button
                                                                                className="btn btn-sm btn-outline-success"
                                                                                onClick={handleUpdateSanction}
                                                                                disabled={savingSanction}
                                                                            >
                                                                                <i className="fa fa-check"></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-outline-secondary"
                                                                                onClick={handleCancelEdit}
                                                                                disabled={savingSanction}
                                                                            >
                                                                                <i className="fa fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                // Modo vista
                                                                <tr key={sanction.idSanction}>
                                                                    <td className="align-middle">
                                                                        <small className="text-muted">{sanction.code}</small>
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {sanction.reason}
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {moment(sanction.startDate).format('DD/MM/YYYY')}
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {moment(sanction.endDate).format('DD/MM/YYYY')}
                                                                    </td>
                                                                    <td className="align-middle">
                                                                        {isSanctionActive(sanction) ? (
                                                                            <Badge bg="danger">Activa</Badge>
                                                                        ) : (
                                                                            <Badge bg="secondary">Inactiva</Badge>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-1">
                                                                            <button
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                onClick={() => handleStartEditSanction(sanction)}
                                                                                disabled={savingSanction || showSanctionForm}
                                                                                title="Editar"
                                                                            >
                                                                                <i className="fa fa-edit"></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                onClick={() => handleDeleteSanction(sanction.idSanction)}
                                                                                disabled={savingSanction}
                                                                                title="Eliminar"
                                                                            >
                                                                                <i className="fa fa-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            !showSanctionForm && (
                                                <div className="text-center py-4 text-muted">
                                                    <i className="fa fa-inbox" style={{ fontSize: '2rem' }}></i>
                                                    <p className="mb-0 mt-2">No hay sanciones registradas para este usuario</p>
                                                </div>
                                            )
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Modal.Body>
                </div>
            </Modal>

            {/* Modal de Detalle de Licencia */}
            {showLicenseDetail && selectedLicenseDetail && (
                <Modal
                    show={showLicenseDetail}
                    onHide={() => setShowLicenseDetail(false)}
                    size="md"
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Detalle de Licencia</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="text-center mb-4">
                            <h5 className="mb-3">{selectedLicenseDetail.licenseCode}</h5>
                            <div className="d-flex justify-content-center gap-2 mb-3">
                                {getLicenseTypeBadge(selectedLicenseDetail.licenseType)}
                                {getLicenseStatusBadge(selectedLicenseDetail)}
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="text-muted small mb-1">MODALIDAD</label>
                            <div className="p-2 bg-light rounded">
                                {selectedLicenseDetail.modality || 'N/A'}
                            </div>
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-6">
                                <label className="text-muted small mb-1">FECHA DE EMISIÓN</label>
                                <div className="p-2 bg-light rounded">
                                    {selectedLicenseDetail.issueDate
                                        ? moment(selectedLicenseDetail.issueDate).format('DD/MM/YYYY')
                                        : 'No definida'}
                                </div>
                            </div>
                            <div className="col-6">
                                <label className="text-muted small mb-1">FECHA DE EXPIRACIÓN</label>
                                <div className="p-2 bg-light rounded">
                                    {selectedLicenseDetail.expirationDate
                                        ? moment(selectedLicenseDetail.expirationDate).format('DD/MM/YYYY')
                                        : 'No definida'}
                                </div>
                            </div>
                        </div>

                        {selectedLicenseDetail.event && (
                            <div className="mb-3">
                                <label className="text-muted small mb-1">EVENTO</label>
                                <div className="p-2 bg-light rounded">
                                    <strong>{selectedLicenseDetail.event.titleEvent}</strong>
                                    <br />
                                    <small className="text-muted">
                                        Año: {selectedLicenseDetail.event.yearEvent}
                                    </small>
                                    <br />
                                    <Badge bg="info" className="mt-1">
                                        {moment(selectedLicenseDetail.event.eventDate).format('DD/MM/YYYY')}
                                    </Badge>
                                </div>
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="text-muted small mb-1">TOKEN DE VALIDACIÓN</label>
                            <div className="p-2 bg-light rounded" style={{
                                wordBreak: 'break-all',
                                fontSize: '0.85rem',
                                fontFamily: 'monospace'
                            }}>
                                {selectedLicenseDetail.validationToken}
                            </div>
                        </div>

                        <div className="mt-4 pt-3 border-top">
                            <small className="text-muted">
                                <strong>Creada:</strong> {moment(selectedLicenseDetail.createdAt).format('DD/MM/YYYY HH:mm')}
                                <br />
                                {selectedLicenseDetail.updatedAt && (
                                    <>
                                        <strong>Actualizada:</strong> {moment(selectedLicenseDetail.updatedAt).format('DD/MM/YYYY HH:mm')}
                                    </>
                                )}
                            </small>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowLicenseDetail(false)}>
                            Cerrar
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
}

export default UserDetailModal;